'use client'

// `useAuth` — the auth-facing selector over the unified SessionProvider.
//
// The former AuthProvider component + TenantProvider were merged into
// `@/components/session/SessionProvider` (BUG-929). This file is kept ONLY so the
// ~20 `import { useAuth } from './AuthProvider'` call sites don't
// have to change; it now selects the auth slice of the single session context.
//
// The dead `isAdmin` / `hasOrgRole` fields (zero consumers) were dropped in the
// same change; `orgMemberships` followed in BL-1109 — `/api/auth/me` stopped
// sending it at the Better Auth cutover (BL-787B), so it had been a constant `[]`
// with no consumer ever since.

import { useMemo } from 'react'
import type { SessionUserDto } from '@auctogon/core-admin/schemas/responses/session'
import { Role } from '@auctogon/admin-isomorphic/types/user'
import { useSessionContext, type AuthStatus, type SignOutOutcome } from '../session/SessionProvider'

/** The auth slice exposed by {@link useAuth}. */
export interface AuthContextValue {
  user: SessionUserDto | null
  /** True until user + tenants + active tenant are ALL resolved (BUG-929). */
  loading: boolean
  /**
   * Resolved auth state. `AuthGuard` redirects to sign-in ONLY on a real
   * `'unauthenticated'`; a transient `'error'` renders a retryable state rather
   * than a mid-session logout (BUG-1182).
   */
  authStatus: AuthStatus
  /** Per-tenant detailed roles `{ [tenantId]: Role[] }` (BUG-211). */
  tenantRoles: Record<string, Role[]>
  /** Revoke the server session, then sign out here; refused offline (BUG-2071). */
  signOut: () => Promise<SignOutOutcome>
  refreshUser: () => Promise<void>
  isSuperAdmin: boolean
}

// Returned when `useAuth` renders outside a SessionProvider (static generation,
// isolated tests). `loading: true` keeps guards in their spinner state rather
// than treating "no provider" as "signed out".
const OUTSIDE_PROVIDER_FALLBACK: AuthContextValue = {
  user: null,
  loading: true,
  authStatus: 'loading',
  tenantRoles: {},
  // No provider, no session to revoke: today's no-op, reported as done.
  signOut: async () => ({ success: true }),
  refreshUser: async () => {},
  isSuperAdmin: false,
}

/**
 * Client-side auth accessor. Reads the auth slice of the unified session context;
 * returns a safe default outside a provider (does NOT throw).
 */
export const useAuth = (): AuthContextValue => {
  const ctx = useSessionContext()
  return useMemo<AuthContextValue>(() => {
    if (!ctx) return OUTSIDE_PROVIDER_FALLBACK
    const { user, loading, authStatus, tenantRoles, signOut, refreshUser, isSuperAdmin } = ctx
    return { user, loading, authStatus, tenantRoles, signOut, refreshUser, isSuperAdmin }
  }, [ctx])
}
